/**
 * usePreflightCost — single source of truth for inline cost badges.
 *
 * Wraps useAccurateTokenCount with:
 *   - Auto-fire on inputs change (debounce-free; key by length signature)
 *   - Standard {credits, isComputing, error} shape — error visible in UI
 *   - Disable via `enabled` flag (skip if button currently running)
 *
 * Usage:
 *   const qc = usePreflightCost({ type: 'qc', sourceSegments, aiSegments });
 *   <InlineCostBadge {...qc} compact />
 */

import { useEffect, useMemo, useState } from 'react';
import { useAccurateTokenCount } from './useAccurateTokenCount';

export type PreflightAgentType = 'translate' | 'retranslate' | 'qc' | 'audit' | 'semantic-match';

export interface PreflightCostOptions {
    type: PreflightAgentType;
    sourceSegments?: string[];
    aiSegments?: string[];
    humanSegments?: string[];
    /** Default true. Set false to skip (e.g. button is currently running). */
    enabled?: boolean;
}

export interface PreflightCostResult {
    credits: number | null;
    isComputing: boolean;
    error: string | null;
}

export function usePreflightCost(opts: PreflightCostOptions): PreflightCostResult {
    const { count } = useAccurateTokenCount();
    const [credits, setCredits] = useState<number | null>(null);
    const [computing, setComputing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const enabled = opts.enabled !== false;
    const srcLen = opts.sourceSegments?.length || 0;
    const aiLen = opts.aiSegments?.length || 0;
    const humLen = opts.humanSegments?.length || 0;

    // Stable signature: re-fetch only when inputs LENGTH changes (not content edits)
    const sigKey = useMemo(() => {
        if (!enabled) return null;
        if (opts.type === 'translate' || opts.type === 'retranslate') {
            return srcLen > 0 ? `${opts.type}:${srcLen}` : null;
        }
        if (opts.type === 'qc') {
            return (srcLen > 0 && aiLen > 0) ? `qc:${srcLen}:${aiLen}` : null;
        }
        if (opts.type === 'audit') {
            return (srcLen > 0 && aiLen > 0 && humLen > 0) ? `audit:${srcLen}:${aiLen}:${humLen}` : null;
        }
        if (opts.type === 'semantic-match') {
            // semantic-match: VI + EN segments. Reuse sourceSegments (vi) + humanSegments (en).
            return (srcLen > 0 && humLen > 0) ? `match:${srcLen}:${humLen}` : null;
        }
        return null;
    }, [enabled, opts.type, srcLen, aiLen, humLen]);

    useEffect(() => {
        if (!sigKey) {
            setCredits(null);
            setError(null);
            setComputing(false);
            return;
        }
        let cancelled = false;
        setComputing(true);
        setError(null);

        // Map UI type → backend agent type. retranslate uses translate API.
        const apiType: 'translate' | 'qc' | 'audit' | 'semantic-match' =
            opts.type === 'retranslate' ? 'translate' : opts.type;

        // Build payload by API contract:
        //  - translate: needs `segments`
        //  - qc/audit:  needs `sourceSegments` + `aiSegments` (+ humanSegments for audit)
        //  - semantic-match: sourceSegments (vi) + aiSegments (en, mapped from humanSegments)
        const payload: any = { type: apiType };
        if (apiType === 'translate') {
            payload.segments = opts.sourceSegments;
        } else if (apiType === 'semantic-match') {
            payload.sourceSegments = opts.sourceSegments;
            payload.aiSegments = opts.humanSegments; // en side
        } else {
            payload.sourceSegments = opts.sourceSegments;
            payload.aiSegments = opts.aiSegments;
            if (apiType === 'audit') {
                payload.humanSegments = opts.humanSegments;
            }
        }

        count(payload)
            .then(r => {
                if (cancelled) return;
                if (!r) {
                    // Hook stored the error in its own state; surface a generic message here
                    setError('Không tính được cost');
                    setCredits(null);
                } else {
                    setCredits(r.estimated_credits);
                    setError(null);
                }
            })
            .catch(e => {
                if (!cancelled) {
                    setError(e?.message || 'Error');
                    setCredits(null);
                }
            })
            .finally(() => {
                if (!cancelled) setComputing(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sigKey, count]);

    return { credits, isComputing: computing, error };
}
